import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../redux/apis/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/profile/view', { _isAuthCheck: true });
            if (res?.data?.data) {
                setUser(res.data.data);
            } else {
                setUser(null);
            }
        } catch (_) {
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    useEffect(() => {
        const handler = () => { setUser(null); navigate('/'); };
        window.addEventListener('auth:session-expired', handler);
        return () => window.removeEventListener('auth:session-expired', handler);
    }, [navigate]);

    const logout = useCallback(async () => {
        try { await api.post('/auth/logout'); } catch (_) { }
        setUser(null);
        localStorage.clear();
        sessionStorage.clear();
        navigate('/');
    }, [navigate]);

    useEffect(() => {
        const IDLE_MS = 30 * 60 * 1000; // 30 minutes
        let timer;
        const reset = () => {
            clearTimeout(timer);
            timer = setTimeout(logout, IDLE_MS);
        };
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        events.forEach(e => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => {
            clearTimeout(timer);
            events.forEach(e => window.removeEventListener(e, reset));
        };
    }, [logout]);

    return (
        <AuthContext.Provider value={{
            user,
            isAdmin: user?.user_type === 'admin',
            isTeamLead: user?.user_type === 'team_lead',
            isAuthenticated: !!user,
            isLoading,
            fetchMe,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}
